import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
	FormBuilder,
	Validators,
	FormGroup,
	FormControl,
} from '@angular/forms';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
	MAT_MOMENT_DATE_ADAPTER_OPTIONS,
	MomentDateAdapter,
} from '@angular/material-moment-adapter';

import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';
import moment from 'moment';

import { BeneficiaryInfoDetailsDto } from '../../../../../modules/beneficiaries/dtos/beneficiary-info-details';
import { BeneficiaryWalletDto } from '../../../../../modules/beneficiaries/dtos/beneficiary-wallet';
import { WalletCreateDto } from '../../../../../modules/wallets/dtos/wallet-create';
import { WalletUpdateDto } from '../../../../../modules/wallets/dtos/wallet-update';
import { WalletsService } from '../../../../../modules/wallets/wallets.service';
import { SnackBarService } from '../../../../../shared/components/snack-bar/snack-bar.service';
import { TypeKinship } from '../../../../../shared/enums/type-kinship.enum';
import { WalletType } from '../../../../../shared/enums/wallet-type.enum';
import { PT_BR_DATE_FORMATS } from '../../../../../utils/date-formats.utils';
import { BeneficiaryInfoResponse } from '../../../models/beneficiaries.model';
import {
	fetchBeneficiaryAllBenefitsStart,
	fetchDependentBenefitsStart,
	restoreFormReset,
} from '../../../state/organizations.actions';
import {
	selectBeneficiaryAllBenefits,
	selectDependentBenefits,
} from '../../../state/organizations.selector';

export interface EditBenefitsModalData {
	beneficiaryId: string;
	conjugated: BeneficiaryWalletDto | null;
	dental: BeneficiaryWalletDto | null;
	dentalCongener: BeneficiaryWalletDto | null;
	health: BeneficiaryWalletDto | null;
	healthCongener: BeneficiaryWalletDto | null;
	isCongener?: boolean;
	orgId: string;
	positionId: string;
	titularId: string | null;
	typeKinship: TypeKinship;
}

export type BenefitsFormGroup = FormGroup<{
	conjugated_card: FormControl<string | null>;
	conjugated_plan: FormControl<string | null>;
	conjugatedDate: FormControl<Date | moment.Moment | string | null>;
	dental_card: FormControl<string | null>;
	dental_congener_card: FormControl<string | null>;
	dental_congener_plan: FormControl<string | null>;
	dental_plan: FormControl<string | null>;
	dentalCongenerDate: FormControl<Date | moment.Moment | string | null>;
	dentalDate: FormControl<Date | moment.Moment | string | null>;
	health_card: FormControl<string | null>;
	health_congener_card: FormControl<string | null>;
	health_congener_plan: FormControl<string | null>;
	health_plan: FormControl<string | null>;
	healthCongenerDate: FormControl<Date | moment.Moment | string | null>;
	healthDate: FormControl<Date | moment.Moment | string | null>;
	showConjugated: FormControl<boolean>;
}>;

interface CreateBenefitsFormData {
	conjugated: BeneficiaryWalletDto | null;
	dental: BeneficiaryWalletDto | null;
	health: BeneficiaryWalletDto | null;
}

function createBenefitsFormGroup(
	fb: FormBuilder,
	data: CreateBenefitsFormData,
): BenefitsFormGroup {
	const hasConjugatedData = !!data.conjugated;

	return fb.group({
		conjugated_card: new FormControl<string | null>({
			disabled: !hasConjugatedData,
			value: null,
		}),
		conjugated_plan: new FormControl<string | null>(null),
		conjugatedDate: new FormControl<Date | moment.Moment | string | null>(null),
		dental_card: new FormControl<string | null>({
			disabled: !!data.dental?.not_aplicable,
			value: data.dental?.not_aplicable
				? null
				: (data.dental?.wallet_number ?? null),
		}),
		dental_congener_card: new FormControl<string | null>(null),
		dental_congener_plan: new FormControl<string | null>(null),
		dental_plan: new FormControl<string | null>(
			data.dental?.not_aplicable
				? 'NAO_APLICAVEL'
				: (data.dental?.benefit_id ?? null),
		),
		dentalCongenerDate: new FormControl<Date | moment.Moment | string | null>(
			null,
		),
		dentalDate: new FormControl<Date | moment.Moment | string | null>(
			data.dental?.updatedInInsuranceAt ?? null,
		),
		health_card: new FormControl<string | null>({
			disabled: !!data.health?.not_aplicable,
			value: data.health?.not_aplicable
				? null
				: (data.health?.wallet_number ?? null),
		}),
		health_congener_card: new FormControl<string | null>(null),
		health_congener_plan: new FormControl<string | null>(null),
		health_plan: new FormControl<string | null>(
			data.health?.not_aplicable
				? 'NAO_APLICAVEL'
				: (data.health?.benefit_id ?? null),
		),
		healthCongenerDate: new FormControl<Date | moment.Moment | string | null>(
			null,
		),
		healthDate: new FormControl<Date | moment.Moment | string | null>(
			data.health?.updatedInInsuranceAt ?? null,
		),
		showConjugated: new FormControl<boolean>(hasConjugatedData, {
			nonNullable: true,
		}),
	}) as BenefitsFormGroup;
}

@Component({
	providers: [
		{ provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
		{
			deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
			provide: DateAdapter,
			useClass: MomentDateAdapter,
		},
		{ provide: MAT_DATE_FORMATS, useValue: PT_BR_DATE_FORMATS },
	],
	selector: 'app-edit-benefits-modal',
	styleUrls: ['./edit-benefits-modal.component.scss'],
	templateUrl: './edit-benefits-modal.component.html',
})
export class EditBenefitsModalComponent implements OnInit, OnDestroy {
	conjugatedBenefits: BeneficiaryInfoDetailsDto[] = [];
	dentalBenefits: BeneficiaryInfoDetailsDto[] = [
		{ id: 'NAO_APLICAVEL', name: 'Benefício não aplicável' },
	];

	form: BenefitsFormGroup;
	healthBenefits: BeneficiaryInfoDetailsDto[] = [
		{ id: 'NAO_APLICAVEL', name: 'Benefício não aplicável' },
	];

	inputId: string = String(Math.random());
	isLoading = false;

	nonAplic: BeneficiaryInfoDetailsDto[] = [
		{ id: 'NAO_APLICAVEL', name: 'Benefício não aplicável' },
	];
	wallets: WalletUpdateDto[] = [];
	walletsToCreate: WalletCreateDto[] = [];

	private onSubmitSubscription$: Subscription = new Subscription();
	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly fb: FormBuilder,
		private readonly store: Store,
		private readonly snackbarService: SnackBarService,
		private readonly walletsService: WalletsService,
		public dialogRef: MatDialogRef<EditBenefitsModalComponent>,
		@Inject(MAT_DIALOG_DATA)
		public data: EditBenefitsModalData,
	) {
		const hasConjugatedData = !!this.data.conjugated;
		this.form = createBenefitsFormGroup(this.fb, this.data);

		// Initialize congener form controls with data
		if (this.data.healthCongener) {
			this.form.patchValue(
				{
					health_congener_card: this.data.healthCongener.wallet_number,
					health_congener_plan: this.data.healthCongener.benefit_id,
					healthCongenerDate: this.data.healthCongener.updatedInInsuranceAt
						? new Date(this.data.healthCongener.updatedInInsuranceAt)
						: null,
				},
				{ emitEvent: false },
			);
		}

		if (this.data.dentalCongener) {
			this.form.patchValue(
				{
					dental_congener_card: this.data.dentalCongener.wallet_number,
					dental_congener_plan: this.data.dentalCongener.benefit_id,
					dentalCongenerDate: this.data.dentalCongener.updatedInInsuranceAt
						? new Date(this.data.dentalCongener.updatedInInsuranceAt)
						: null,
				},
				{ emitEvent: false },
			);
		}

		this.form
			.get('showConjugated')!
			.valueChanges.pipe(takeUntil(this.unsubscribe$))
			.subscribe((isConjugatedMode: boolean) => {
				this.updateFormValidators(isConjugatedMode);
			});
		this.updateFormValidators(hasConjugatedData);
	}

	closeModal() {
		this.dialogRef.close();
	}

	createWallets(wallets: WalletCreateDto[]): void {
		this.isLoading = true;

		this.onSubmitSubscription$ = this.walletsService
			.createManyWallets({
				dto: wallets,
			})
			.subscribe(
				async () => {
					this.dialogRef.close(true);
					this.isLoading = false;
					this.walletsToCreate = [];
				},
				(error: HttpErrorResponse) => {
					this.snackbarService.showSnack(error.error?.message, 'error');
					this.isLoading = false;
					this.walletsToCreate = [];
				},
			);
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.store.dispatch(restoreFormReset());

		this.onSubmitSubscription$.unsubscribe();
	}

	ngOnInit(): void {
		if (this.data.conjugated) {
			this.form.patchValue(
				{
					conjugated_card: this.data.conjugated.wallet_number,
					conjugated_plan: this.data.conjugated.benefit_id,
					conjugatedDate: this.data.conjugated.updatedInInsuranceAt
						? new Date(this.data.conjugated.updatedInInsuranceAt)
						: null,
				},
				{ emitEvent: false },
			);
		}

		this.validateUpdatedData('dental');
		this.validateUpdatedData('health');
		this.validateUpdatedData('conjugated');

		this.updateForm('health', this.form.get('health_plan')?.value ?? null);
		this.updateForm('dental', this.form.get('dental_plan')?.value ?? null);
		if (this.form.get('showConjugated')?.value) {
			this.updateForm(
				'conjugated',
				this.form.get('conjugated_plan')?.value ?? null,
			);
		}

		this.store
			.select(
				this.data.typeKinship === TypeKinship.TITULAR
					? selectBeneficiaryAllBenefits
					: selectDependentBenefits,
			)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((beneficiaryInfo: BeneficiaryInfoResponse | null) => {
				if (beneficiaryInfo) {
					this.healthBenefits = this.nonAplic.concat(beneficiaryInfo.healths);
					this.dentalBenefits = this.nonAplic.concat(beneficiaryInfo.teeths);
					if (beneficiaryInfo.conjugated) {
						this.conjugatedBenefits = beneficiaryInfo.conjugated;
					}
					this.isLoading = false;
				}
			});

		if (this.data.typeKinship === TypeKinship.TITULAR) {
			this.store.dispatch(
				fetchBeneficiaryAllBenefitsStart({
					id: this.data.orgId,
					positionId: this.data.positionId,
				}),
			);
		} else {
			this.store.dispatch(
				fetchDependentBenefitsStart({
					id: this.data.titularId ?? '',
				}),
			);
		}
	}

	onContinue(): void {
		this.wallets = [];
		this.walletsToCreate = [];

		if (this.form.invalid) {
			this.updateValidation();

			return;
		}

		const formVal = this.form.getRawValue();
		const isConjugatedMode = formVal.showConjugated;

		if (isConjugatedMode) {
			const conjugatedDate = formVal.conjugatedDate
				? moment(formVal.conjugatedDate).format('YYYY-MM-DD HH:mm:ssZZ')
				: null;
			const selectedConjugatedPlanId =
				formVal.conjugated_plan === 'NAO_APLICAVEL'
					? null
					: formVal.conjugated_plan;
			const walletNumberConjugated = formVal.conjugated_card;
			const walletTypeForConjugated = this.data.isCongener
				? WalletType.CONGENER
				: WalletType.CONJUGATED;

			if (this.data.conjugated?.id) {
				this.wallets.push(
					new WalletUpdateDto({
						id: this.data.conjugated.id,
						benefitId: selectedConjugatedPlanId,
						isHealth: true,
						isNotApplicable: !selectedConjugatedPlanId,
						isTeeth: false,
						type: walletTypeForConjugated,
						updatedInInsuranceAt: conjugatedDate,
						walletNumber: walletNumberConjugated,
					}),
				);
			} else {
				if (selectedConjugatedPlanId) {
					this.walletsToCreate.push(
						new WalletCreateDto({
							beneficiaryId: this.data.beneficiaryId,
							benefitId: selectedConjugatedPlanId,
							isHealth: true,
							isNotApplicable: false,
							isTeeth: false,
							type: walletTypeForConjugated,
							updatedInInsuranceAt: conjugatedDate,
							walletNumber: walletNumberConjugated,
						}),
					);
				}
			}
		} else {
			const healthDate = formVal?.healthDate
				? moment(formVal?.healthDate).format('YYYY-MM-DD HH:mm:ssZZ')
				: null;
			const dentalDate = formVal?.dentalDate
				? moment(formVal?.dentalDate).format('YYYY-MM-DD HH:mm:ssZZ')
				: null;

			const healthPlanSelected =
				formVal.health_plan === 'NAO_APLICAVEL' ? null : formVal.health_plan;
			const isHealthNotApplicable = formVal.health_plan === 'NAO_APLICAVEL';
			const healthCardValue = isHealthNotApplicable
				? null
				: formVal.health_card;

			const dentalPlanSelected =
				formVal.dental_plan === 'NAO_APLICAVEL' ? null : formVal.dental_plan;
			const isDentalNotApplicable = formVal.dental_plan === 'NAO_APLICAVEL';
			const dentalCardValue = isDentalNotApplicable
				? null
				: formVal.dental_card;

			const walletTypeForDefault = this.data.isCongener
				? WalletType.CONGENER
				: WalletType.DEFAULT;

			if (!this.data.health) {
				if (healthPlanSelected || isHealthNotApplicable) {
					this.walletsToCreate.push(
						new WalletCreateDto({
							beneficiaryId: this.data.beneficiaryId,
							benefitId: healthPlanSelected,
							isHealth: true,
							isNotApplicable: isHealthNotApplicable,
							isTeeth: false,
							type: walletTypeForDefault,
							updatedInInsuranceAt: healthDate,
							walletNumber: healthCardValue,
						}),
					);
				}
			} else {
				this.wallets.push(
					new WalletUpdateDto({
						id: this.data.health.id,
						benefitId: healthPlanSelected,
						isHealth: true,
						isNotApplicable: isHealthNotApplicable,
						isTeeth: false,
						type: walletTypeForDefault,
						updatedInInsuranceAt: healthDate,
						walletNumber: healthCardValue,
					}),
				);
			}

			if (!this.data.dental) {
				if (dentalPlanSelected || isDentalNotApplicable) {
					this.walletsToCreate.push(
						new WalletCreateDto({
							beneficiaryId: this.data.beneficiaryId,
							benefitId: dentalPlanSelected,
							isHealth: false,
							isNotApplicable: isDentalNotApplicable,
							isTeeth: true,
							type: walletTypeForDefault,
							updatedInInsuranceAt: dentalDate,
							walletNumber: dentalCardValue,
						}),
					);
				}
			} else {
				this.wallets.push(
					new WalletUpdateDto({
						id: this.data.dental.id,
						benefitId: dentalPlanSelected,
						isHealth: false,
						isNotApplicable: isDentalNotApplicable,
						isTeeth: true,
						type: walletTypeForDefault,
						updatedInInsuranceAt: dentalDate,
						walletNumber: dentalCardValue,
					}),
				);
			}
		}

		// Handle congener benefits
		if (this.data.healthCongener) {
			const healthCongenerPlan =
				formVal.health_congener_plan === 'NAO_APLICAVEL'
					? null
					: formVal.health_congener_plan;
			const isHealthCongenerNotApplicable =
				formVal.health_congener_plan === 'NAO_APLICAVEL';
			const healthCongenerCard = isHealthCongenerNotApplicable
				? null
				: formVal.health_congener_card;
			const healthCongenerDate = formVal.healthCongenerDate
				? moment(formVal.healthCongenerDate).format('YYYY-MM-DD HH:mm:ssZZ')
				: null;

			this.wallets.push(
				new WalletUpdateDto({
					id: this.data.healthCongener.id,
					benefitId: healthCongenerPlan,
					isHealth: true,
					isNotApplicable: isHealthCongenerNotApplicable,
					isTeeth: false,
					type: WalletType.CONGENER,
					updatedInInsuranceAt: healthCongenerDate,
					walletNumber: healthCongenerCard,
				}),
			);
		}

		if (this.data.dentalCongener) {
			const dentalCongenerPlan =
				formVal.dental_congener_plan === 'NAO_APLICAVEL'
					? null
					: formVal.dental_congener_plan;
			const isDentalCongenerNotApplicable =
				formVal.dental_congener_plan === 'NAO_APLICAVEL';
			const dentalCongenerCard = isDentalCongenerNotApplicable
				? null
				: formVal.dental_congener_card;
			const dentalCongenerDate = formVal.dentalCongenerDate
				? moment(formVal.dentalCongenerDate).format('YYYY-MM-DD HH:mm:ssZZ')
				: null;

			this.wallets.push(
				new WalletUpdateDto({
					id: this.data.dentalCongener.id,
					benefitId: dentalCongenerPlan,
					isHealth: false,
					isNotApplicable: isDentalCongenerNotApplicable,
					isTeeth: true,
					type: WalletType.CONGENER,
					updatedInInsuranceAt: dentalCongenerDate,
					walletNumber: dentalCongenerCard,
				}),
			);
		}

		const finalWalletsToCreate = this.walletsToCreate.filter(
			(w) => w.benefitId || w.isNotApplicable,
		);
		const finalWalletsToUpdate = this.wallets.filter(
			(w) => w.id && (w.benefitId !== undefined || w.isNotApplicable),
		);

		if (finalWalletsToCreate.length > 0) {
			this.createWallets(finalWalletsToCreate);
		}

		if (finalWalletsToUpdate.length > 0) {
			this.updateManyWallets(finalWalletsToUpdate);
		}

		if (
			finalWalletsToCreate.length === 0 &&
			finalWalletsToUpdate.length === 0
		) {
			this.closeModal();
		}
	}

	searchBenefitName(isHealth: boolean): string {
		if (isHealth) {
			const selectedPlan = this.form.get('health_plan')?.value;

			return (
				this.healthBenefits.find((benefit) => benefit.id === selectedPlan)
					?.name || ''
			);
		} else {
			const selectedPlan = this.form.get('dental_plan')?.value;

			return (
				this.dentalBenefits.find((benefit) => benefit.id === selectedPlan)
					?.name || ''
			);
		}
	}

	updateForm(
		type: 'conjugated' | 'dental' | 'health',
		plan: string | null,
	): void {
		const cardControl = this.form.get(`${type}_card`)!;
		const dateControl = this.form.get(`${type}Date`)!;

		if (plan === 'NAO_APLICAVEL') {
			cardControl.clearValidators();
			cardControl.setValue(null);
			cardControl.disable();

			dateControl.clearValidators();
			dateControl.setValue(null);
			dateControl.disable();
		} else {
			cardControl.enable();

			dateControl.enable();
			dateControl.setValidators([Validators.required]);
		}
		cardControl.updateValueAndValidity({ onlySelf: true });
		dateControl.updateValueAndValidity({ onlySelf: true });
	}

	updateFormValidators(isConjugatedMode: boolean): void {
		const healthPlanControl = this.form.get('health_plan')!;
		const healthCardControl = this.form.get('health_card')!;
		const healthDateControl = this.form.get('healthDate')!;
		const dentalPlanControl = this.form.get('dental_plan')!;
		const dentalCardControl = this.form.get('dental_card')!;
		const dentalDateControl = this.form.get('dentalDate')!;
		const conjugatedPlanControl = this.form.get('conjugated_plan')!;
		const conjugatedCardControl = this.form.get('conjugated_card')!;
		const conjugatedDateControl = this.form.get('conjugatedDate')!;

		if (isConjugatedMode) {
			[
				healthPlanControl,
				healthDateControl,
				dentalPlanControl,
				dentalDateControl,
			].forEach((control) => {
				control.clearValidators();
				control.setValue(null, { emitEvent: false });
			});
			healthPlanControl.setValue('NAO_APLICAVEL', { emitEvent: false });
			dentalPlanControl.setValue('NAO_APLICAVEL', { emitEvent: false });
			healthCardControl.setValue(null, { emitEvent: false });
			healthCardControl.disable({ emitEvent: false });
			dentalCardControl.setValue(null, { emitEvent: false });
			dentalCardControl.disable({ emitEvent: false });

			conjugatedPlanControl.setValidators([Validators.required]);
			if (
				conjugatedPlanControl.value !== 'NAO_APLICAVEL' &&
				conjugatedPlanControl.value !== null
			) {
				conjugatedCardControl.enable({ emitEvent: false });
				conjugatedDateControl.setValidators([Validators.required]);
			} else {
				conjugatedCardControl.disable({ emitEvent: false });
				conjugatedCardControl.clearValidators();
				conjugatedCardControl.setValue(null, { emitEvent: false });
				conjugatedDateControl.clearValidators();
				conjugatedDateControl.setValue(null, { emitEvent: false });
				conjugatedDateControl.disable({ emitEvent: false });
			}
		} else {
			[conjugatedPlanControl, conjugatedDateControl].forEach((control) => {
				control.clearValidators();
				control.setValue(null, { emitEvent: false });
			});
			conjugatedPlanControl.setValue('NAO_APLICAVEL', { emitEvent: false });
			conjugatedCardControl.setValue(null, { emitEvent: false });
			conjugatedCardControl.disable({ emitEvent: false });

			healthPlanControl.setValidators([Validators.required]);
			dentalPlanControl.setValidators([Validators.required]);

			if (
				healthPlanControl.value !== 'NAO_APLICAVEL' &&
				healthPlanControl.value !== null
			) {
				healthCardControl.enable({ emitEvent: false });
				healthDateControl.setValidators([Validators.required]);
			} else {
				healthCardControl.disable({ emitEvent: false });
				healthCardControl.setValue(null, { emitEvent: false });
				healthDateControl.clearValidators();
				healthDateControl.setValue(null, { emitEvent: false });
			}

			if (
				dentalPlanControl.value !== 'NAO_APLICAVEL' &&
				dentalPlanControl.value !== null
			) {
				dentalCardControl.enable({ emitEvent: false });
				dentalDateControl.setValidators([Validators.required]);
			} else {
				dentalCardControl.disable({ emitEvent: false });
				dentalCardControl.setValue(null, { emitEvent: false });
				dentalDateControl.clearValidators();
				dentalDateControl.setValue(null, { emitEvent: false });
				dentalDateControl.disable({ emitEvent: false });
			}
		}
		[
			healthPlanControl,
			healthCardControl,
			healthDateControl,
			dentalPlanControl,
			dentalCardControl,
			dentalDateControl,
			conjugatedPlanControl,
			conjugatedCardControl,
			conjugatedDateControl,
		].forEach((control) => {
			control.updateValueAndValidity({ emitEvent: false });
		});
		this.validateUpdatedData('health');
		this.validateUpdatedData('dental');
		this.validateUpdatedData('conjugated');
	}

	updateManyWallets(wallets: WalletUpdateDto[]): void {
		this.isLoading = true;
		this.onSubmitSubscription$ = this.walletsService
			.updateManyWallets({ dto: wallets })
			.subscribe(
				async () => {
					this.snackbarService.showSnack('Benefícios atualizados', 'success');
					this.dialogRef.close(true);
					this.isLoading = false;
					this.wallets = [];
				},
				(error: HttpErrorResponse) => {
					this.snackbarService.showSnack(error.error?.message, 'error');
					this.isLoading = false;
					this.wallets = [];
				},
			);
	}

	updateValidation(): void {
		for (const control in this.form.controls) {
			this.form.get(control)!.markAsTouched();
			this.form.get(control)!.updateValueAndValidity({ onlySelf: true });
		}
	}

	validateUpdatedData(type: 'conjugated' | 'dental' | 'health'): void {
		const typePlanValue = this.form.get(`${type}_plan`)?.value;
		const originalWalletData = this.data[
			type as keyof typeof this.data
		] as BeneficiaryWalletDto | null;

		const dateControl = this.form.get(`${type}Date`);

		if (!dateControl) return;

		if (typePlanValue === 'NAO_APLICAVEL') {
			dateControl.clearValidators();
		} else if (
			typePlanValue !== originalWalletData?.benefit_id ||
			this.form.get(`${type}_card`)?.value !==
				originalWalletData?.wallet_number ||
			(originalWalletData &&
				!originalWalletData.updatedInInsuranceAt &&
				dateControl.value === null) ||
			(originalWalletData?.updatedInInsuranceAt &&
				moment(dateControl.value).format() !==
					moment(originalWalletData.updatedInInsuranceAt).format())
		) {
			if (typePlanValue && typePlanValue !== 'NAO_APLICAVEL') {
				dateControl.setValidators([Validators.required]);
			} else {
				dateControl.clearValidators();
			}
		} else {
			dateControl.clearValidators();
		}
		dateControl.updateValueAndValidity({ onlySelf: true });
	}
}
